import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../config/api';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, Heart, Fire, TShirt, Flag, Sticker, FileText,
  Moon, Sun, Bell, ShoppingBag, Star, Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'all', label: 'All', icon: ShoppingBag },
  { id: 'apparel', label: 'Apparel', icon: TShirt },
  { id: 'yard', label: 'Yard Gear', icon: Flag },
  { id: 'digital', label: 'Digital', icon: FileText },
];

const TAGLINES = [
  { text: "Know Your Number", style: "bold" },
  { text: "Mow Different", style: "italic" },
  { text: "Trust the Data. Feed the Lawn.", style: "clean" },
  { text: "Touch Grass (Seriously)", style: "playful" },
  { text: "Dial In Your Turf", style: "pro" },
  { text: "Grow Smarter", style: "minimal" },
  { text: "I Check My GDD Before I Check My Email", style: "bold" },
  { text: "Data-Driven Lawn Care", style: "clean" },
];

const PulseIcon = ({ size = 40, color = "#0D9256" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <circle cx="50" cy="50" r="45" stroke={color} strokeWidth="3" fill="none" />
    <path d="M 0 50 L 25 50 L 30 50 L 35 35 L 40 65 L 45 20 L 50 80 L 55 45 L 60 50 L 100 50"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const ProductCard = ({ product, isVoted, voteCount, onVote, onNotify }) => {
  const hasImage = !!product.image;

  return (
    <Card
      className="group relative overflow-hidden border border-border/40 hover:border-primary/30 transition-all duration-300 bg-card hover:shadow-lg"
      data-testid={`merch-card-${product.id}`}
    >
      {product.badge && (
        <div className="absolute top-3 right-3 z-10">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full
            ${product.badge === 'FREE' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
              product.badge === 'NEW' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' :
              product.badge === 'SIGNATURE' ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' :
              product.badge === 'FAN FAVORITE' ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400' :
              product.badge === 'BEST VALUE' ? 'bg-violet-500/20 text-violet-600 dark:text-violet-400' :
              'bg-primary/10 text-primary'
            }`}
          >
            {product.badge}
          </span>
        </div>
      )}

      {/* Image or Placeholder */}
      <div className="relative aspect-square overflow-hidden bg-muted/30">
        {hasImage ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-emerald-950/90 to-slate-900">
            <PulseIcon size={56} color="#10b981" />
            <span className="text-emerald-400/80 text-xs uppercase tracking-widest font-medium">
              {product.category === 'digital' ? 'Digital Download' : 'Coming Soon'}
            </span>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Eye size={32} className="text-white drop-shadow-lg" weight="bold" />
        </div>
      </div>

      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-foreground leading-tight truncate">
              {product.name}
            </h3>
            <p className="text-xs text-primary/80 font-medium mt-0.5 italic">
              "{product.tagline}"
            </p>
          </div>
          <span className={`text-sm font-bold shrink-0 ${product.price_range === 'FREE' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>
            {product.price_range}
          </span>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
          {product.description}
        </p>

        {product.colors && product.colors.length > 0 && (
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {product.colors.map(c => (
              <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {c}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant={isVoted ? "default" : "outline"}
            size="sm"
            className={`flex-1 gap-1.5 text-xs ${isVoted ? 'bg-rose-500 hover:bg-rose-600 border-rose-500' : ''}`}
            onClick={() => onVote(product.id)}
            data-testid={`merch-vote-${product.id}`}
          >
            <Heart size={14} weight={isVoted ? "fill" : "regular"} />
            {isVoted ? 'Voted' : 'Want This'}
            {voteCount > 0 && <span className="ml-1 opacity-70">({voteCount})</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-xs gap-1"
            onClick={() => onNotify(product.id)}
            data-testid={`merch-notify-${product.id}`}
          >
            <Bell size={14} />
            Notify Me
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const MerchPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [products, setProducts] = useState([]);
  const [voteCounts, setVoteCounts] = useState({});
  const [userVotes, setUserVotes] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, voteRes] = await Promise.all([
        fetch(`${API_URL}/api/merch/products`, { credentials: 'include' }),
        fetch(`${API_URL}/api/merch/votes`, { credentials: 'include' }),
      ]);
      const prodData = await prodRes.json();
      const voteData = await voteRes.json();
      setProducts(prodData.products || []);
      setVoteCounts(voteData.counts || {});
      setUserVotes(voteData.user_votes || []);
    } catch (err) {
      console.error('Failed to load merch:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVote = async (productId) => {
    try {
      const res = await fetch(`${API_URL}/api/merch/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ product_id: productId }),
      });
      const data = await res.json();

      if (data.action === 'added') {
        setUserVotes(prev => [...prev, productId]);
        toast.success('Vote registered! We hear you.');
      } else {
        setUserVotes(prev => prev.filter(id => id !== productId));
        toast.info('Vote removed.');
      }
      setVoteCounts(prev => ({ ...prev, [productId]: data.vote_count }));
    } catch {
      toast.error('Could not register vote.');
    }
  };

  const handleNotify = async (productId) => {
    try {
      await fetch(`${API_URL}/api/merch/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ product_id: productId }),
      });
      toast.success("You'll be first to know when this drops!");
    } catch {
      toast.error('Could not subscribe.');
    }
  };

  const filtered = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  const topVoted = [...products]
    .sort((a, b) => (voteCounts[b.id] || 0) - (voteCounts[a.id] || 0))
    .slice(0, 3)
    .filter(p => (voteCounts[p.id] || 0) > 0);

  return (
    <div className="min-h-screen bg-background" data-testid="merch-page">
      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="merch-back-btn"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <TurfPulseLogoSVG animated size={28} />
              <span className="font-semibold text-base tracking-tight hidden sm:inline">TurfPulse</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="merch-theme-toggle">
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900" />
        <div className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'radial-gradient(circle at 25% 40%, rgba(16,185,129,0.5) 0%, transparent 50%), radial-gradient(circle at 75% 60%, rgba(16,185,129,0.3) 0%, transparent 50%)',
          }}
        />
        {/* EKG decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-12 opacity-20 overflow-hidden">
          <svg viewBox="0 0 1200 40" className="w-full h-full" preserveAspectRatio="none">
            <path
              d="M0 20 L500 20 L540 20 L560 8 L580 32 L600 2 L620 38 L640 16 L660 20 L1200 20"
              stroke="#10b981" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"
            >
              <animate attributeName="stroke-dashoffset" from="2400" to="0" dur="4s" repeatCount="indefinite" />
              <animate attributeName="stroke-dasharray" values="0 2400;2400 0" dur="4s" repeatCount="indefinite" />
            </path>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full border border-emerald-400/20">
              Merch Lab
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
            Rep Your <span className="text-emerald-400">Lawn Game</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
            Vote on the gear you want to see. The most-voted concepts get produced first.
            <br className="hidden sm:block" />
            Your vote shapes the drop.
          </p>
          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Fire size={14} className="text-orange-400" /> Print-on-demand</span>
            <span className="flex items-center gap-1"><Star size={14} className="text-amber-400" /> Premium quality</span>
            <span className="flex items-center gap-1"><Sticker size={14} className="text-emerald-400" /> Community-driven</span>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Tagline Brainstorm Strip */}
        <div className="mb-10" data-testid="merch-taglines">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            Tagline Concepts
          </h2>
          <div className="flex flex-wrap gap-2">
            {TAGLINES.map(({ text, style }) => (
              <span
                key={text}
                className={`inline-block px-4 py-2 rounded-full text-sm border border-border/50 bg-card
                  ${style === 'bold' ? 'font-bold' : ''}
                  ${style === 'italic' ? 'italic' : ''}
                  ${style === 'playful' ? 'font-medium text-amber-600 dark:text-amber-400' : ''}
                  ${style === 'pro' ? 'font-semibold text-primary' : ''}
                  ${style === 'minimal' ? 'text-muted-foreground font-light' : ''}
                  ${style === 'clean' ? 'font-medium' : ''}
                  hover:border-primary/40 transition-colors cursor-default
                `}
              >
                {text}
              </span>
            ))}
          </div>
        </div>

        {/* Top Voted (if any) */}
        {topVoted.length > 0 && (
          <div className="mb-10" data-testid="merch-top-voted">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
              <Fire size={16} className="text-orange-500" /> Community Favorites
            </h2>
            <div className="flex flex-wrap gap-3">
              {topVoted.map((p, i) => (
                <div key={p.id} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/50">
                  <span className="text-lg font-bold text-primary">#{i + 1}</span>
                  <span className="text-sm font-medium">{p.name}</span>
                  <span className="text-xs text-muted-foreground">({voteCounts[p.id]} votes)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide" data-testid="merch-category-filter">
          {CATEGORIES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveCategory(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all
                ${activeCategory === id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-card border border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/30'
                }`}
              data-testid={`merch-filter-${id}`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="merch-product-grid">
            {filtered.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                isVoted={userVotes.includes(product.id)}
                voteCount={voteCounts[product.id] || 0}
                onVote={handleVote}
                onNotify={handleNotify}
              />
            ))}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No products in this category yet.</p>
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-12 sm:mt-16 relative overflow-hidden rounded-2xl border border-emerald-500/20" data-testid="merch-cta-banner">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 to-slate-900" />
          <div className="relative z-10 p-6 sm:p-10 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
              Got a merch idea?
            </h2>
            <p className="text-sm text-slate-400 mb-5 max-w-lg mx-auto">
              We're building this together. Drop your idea and if the community backs it, we'll make it real.
            </p>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
              onClick={() => {
                const idea = prompt("What merch would you love to see? (Product idea, tagline, design concept)");
                if (idea && idea.trim()) {
                  fetch(`${API_URL}/api/merch/notify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ product_id: 'custom-idea', idea: idea.trim() }),
                  }).then(() => toast.success('Idea submitted! Thanks for the input.')).catch(() => {});
                }
              }}
              data-testid="merch-submit-idea-btn"
            >
              Submit an Idea
            </Button>
          </div>
        </div>

        {/* Brand Identity Section */}
        <div className="mt-12 sm:mt-16" data-testid="merch-brand-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-6">
            Brand Identity
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Logo */}
            <Card className="border border-border/40">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  <PulseIcon size={64} />
                </div>
                <h3 className="font-semibold text-sm mb-1">The Pulse Icon</h3>
                <p className="text-xs text-muted-foreground">
                  Clean heartbeat/EKG line inside a circle. Represents data-driven lawn vitality.
                </p>
              </CardContent>
            </Card>

            {/* Colors */}
            <Card className="border border-border/40">
              <CardContent className="p-6">
                <h3 className="font-semibold text-sm mb-3 text-center">Brand Palette</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-[#0D9256]" />
                    <span className="text-xs text-muted-foreground">TurfPulse Green (#0D9256)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-slate-900" />
                    <span className="text-xs text-muted-foreground">Slate (#0F172A)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-[#10b981]" />
                    <span className="text-xs text-muted-foreground">Accent Emerald (#10B981)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-white border border-border" />
                    <span className="text-xs text-muted-foreground">Clean White</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Typography */}
            <Card className="border border-border/40">
              <CardContent className="p-6">
                <h3 className="font-semibold text-sm mb-3 text-center">Typography</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-['Outfit'] text-xl font-bold tracking-tight">Outfit Bold</p>
                    <p className="text-[10px] text-muted-foreground">Headlines & product names</p>
                  </div>
                  <div>
                    <p className="text-base font-medium">DM Sans Medium</p>
                    <p className="text-[10px] text-muted-foreground">Body copy & descriptions</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Tracking Wide</p>
                    <p className="text-[10px] text-muted-foreground">Badges & labels</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};
